import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  FDBox, FDContentContainer, FDStack,
} from '@facet-wealth/design';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import PageHeader from 'Components/ComponentLibrary/Molecules/PageHeader';
import { useModalContextSubmissionSuccessful } from 'Hooks/useRefreshEvent';
import { CompleteActionItemBanner } from 'Components/ComponentLibrary/Organisms/CompleteActionItemBanner';
import fr from 'facet-rest';
import { useHistory } from 'react-router-dom';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import { navigateToPage } from 'Views/Page/Content/Templates';
import useMobileLayout from 'Hooks/useMobileLayout';
import { buildResourceFromQueryMeta } from 'Components/ComponentLibrary/Molecules/CollectionQueryForm';
import { HouseholdBalanceSheetPageEmptyState } from './HouseholdBalanceSheetEmptyState';
import { BalanceSheetSection } from './BalanceSheetSection';
import { HouseholdBalanceSheetPageActions } from './HouseholdBalanceSheetPageActions';

const getPageMaxWidthBasedOnNumSections = (numSections) => {
  switch (numSections) {
    case 1:
      return 'md';
    default:
      return 'xl';
  }
};

const getSectionMaxWidthBasedOnNumSections = (numSections, { condensed }) => {
  if (condensed) {
    return '100%';
  }
  switch (numSections) {
    case 1:
      return '100%';
    case 2:
      return '50%';
    default:
      return null;
  }
};

// A number this large will cause the UI to have to adjust a bit to fit it
const A_BIG_NUMBER = 100_000_000;

export const HouseholdBalanceSheetPage = ({
  collection, setCollection, resource, refresh, navigate, condensed,
}) => {
  const { isAgent } = useUserContext();
  const isMobile = useMobileLayout();
  const { loadModalCollectionByLink, clearModalCollection } = useModalContext();
  const history = useHistory();
  const { householdResource } = useSimpleHousehold();
  const sections = useMemo(() => resource.attr('sections').resources().list(), [resource]);
  const categories = useMemo(() => sections.flatMap(section => section.attr('categories').resources().list()), [sections]);
  const hasBigNumbers = useMemo(() => categories.flatMap(category => category.attr('lineItems').resources().list()).some(lineItem => +lineItem.attr('value').replace(/[^0-9.]/g, '') >= A_BIG_NUMBER), [sections]);
  const [expandedCategoryIDs, setExpandedCategoryIDs] = useState(new Set(isAgent ? categories.map(c => c.id()) : []));

  useEffect(() => {
    const availableCategories = new Set(categories.map(category => category.id()));
    if (Array.from(expandedCategoryIDs.values()).some(expandedID => !availableCategories.has(expandedID))) {
      // There are categories that are expanded but do not exist.
      // For example, perhaps an agent was looking at the balance sheet of household 1, then switched to the balance sheet of household 2.
      // Remove all such differences
      setExpandedCategoryIDs((ids) => {
        let newSet = new Set(ids); // Needed to make a different pointer in memory so that React knows this has changed
        for (const value of newSet) {
          if (!availableCategories.has(value)) {
            newSet.delete(value);
          }
        }
        // If this is just an agent changing the household ID and now everything is collapsed, expand everything for this new household
        if (newSet.size === 0 && isAgent) {
          newSet = new Set(categories.map(c => c.id()));
        }
        return newSet;
      });
    }
  }, [categories, expandedCategoryIDs]);

  useModalContextSubmissionSuccessful((evt, refreshDependency, clearModalCollectionDependency) => {
    switch (evt.detail.response.meta().type()) {
      case 'realAsset':
        refreshDependency();
        clearModalCollectionDependency();
        break;
      case 'NoopFinishStep':
        refreshDependency();
        break;

      /* no default */
    }
  }, [refresh, clearModalCollection]);

  useEffect(() => {
    resource.withRelationship(fr.relationships.actionLink.name,
      (rel) => loadModalCollectionByLink(rel.link('related').url()),
      () => null);
  }, []);

  const onAddAccount = (rel) => loadModalCollectionByLink(rel.link('related').url());

  const onAddRealAsset = (rel) => loadModalCollectionByLink(rel.link('related').url());

  const isFiltering = useMemo(() => {
    const resourceFromQueryMeta = buildResourceFromQueryMeta(collection, collection.meta().queries().head().export());
    const multipicklists = resourceFromQueryMeta.meta().template().list().filter(field => field.attr('type') === 'multipicklist');
    // count the number of selected filters from query
    const numFilters = multipicklists.flatMap(field => resourceFromQueryMeta.attr(field.id()) ?? []).length;
    return numFilters > 0;
  }, [collection]);

  const onEditLineItemClick = useCallback((rel) => {
    const url = rel.link('related').url();
    if (rel.meta('modal') === 'true') {
      return loadModalCollectionByLink(url);
    }
    return navigateToPage(url, isAgent);
  }, [loadModalCollectionByLink]);

  if (resource.isTruthy('isEmptyState') && !isFiltering) {
    return (
      <React.Fragment>
        <CompleteActionItemBanner resource={resource} />
        <HouseholdBalanceSheetPageEmptyState
          balanceSheetResource={resource}
          onAddAccount={onAddAccount}
          onAddRealAsset={onAddRealAsset}
        />
      </React.Fragment>
    );
  }

  const pageMaxWidth = getPageMaxWidthBasedOnNumSections(resource.attr('sections').resources().length());
  const sectionMaxWidth = getSectionMaxWidthBasedOnNumSections(resource.attr('sections').resources().length(), { condensed });

  const actions = (
    <HouseholdBalanceSheetPageActions
      collection={collection}
      setCollection={setCollection}
      onToggleExpandAll={() => {
        if (expandedCategoryIDs.size > 0) {
          setExpandedCategoryIDs(new Set());
          return;
        }
        setExpandedCategoryIDs(new Set(categories.map(c => c.id())));
      }}
      atLeastOneOpen={expandedCategoryIDs.size > 0}
      history={history}
      householdResource={householdResource}
      balanceSheetResource={resource}
      onAddAccount={onAddAccount}
      onViewConnectionsPage={(rel) => navigate(rel.link('related'))}
    />
  );

  return (
    <React.Fragment>
      <CompleteActionItemBanner resource={resource} />
      <FDContentContainer maxWidth={pageMaxWidth}>
        <FDBox paddingY={2}>
          <FDStack direction="column" spacing={2}>
            <PageHeader
              title={resource.attr('name')}
              subtitle={isMobile ? undefined : resource.attr('description')}
            />
            {actions}
            <FDStack wrap spacing={2} justifyContent={'center'} alignItems={'flex-start'} direction={isMobile ? 'column' : 'row'}>
              {sections.map((sectionResource) => {
                return (
                  <BalanceSheetSection
                    hasBigNumbers={hasBigNumbers}
                    expandedCategoryIDs={expandedCategoryIDs}
                    setExpandedCategoryIDs={setExpandedCategoryIDs}
                    key={sectionResource.id()}
                    sectionResource={sectionResource}
                    onEditLineItemClick={onEditLineItemClick}
                    maxWidthOverride={sectionMaxWidth}
                    isFiltering={isFiltering}
                  />
                );
              })}
            </FDStack>
          </FDStack>
        </FDBox>
      </FDContentContainer>
    </React.Fragment>
  );
};

HouseholdBalanceSheetPage.defaultProps = {
  condensed: false,
};

HouseholdBalanceSheetPage.propTypes = {
  collection: PropTypes.object.isRequired,
  setCollection: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  refresh: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  condensed: PropTypes.bool,
};
